// Hazard helpers that touch the sim (imported lazily by the map modules at call time, never at load
// time, so the maps ↔ sim import cycle is safe from any entry point):
//  - `hazardKick`: the impulse every map hazard uses. Server: a §2.11 hazard hit through
//    `applyKnockback` (Dizzy, HIT, credit refresh, `MODES.onHit`, state responses), rate-limited
//    per (bean, hazard key). Predict mode: the same velocity and tumble on the local bean only, so
//    environment contact is predicted exactly (§0 "every hazard is a pure function of the tick").
//  - `hazardNudge`: radial pops (bubbles) with the same server/predict split.
//  - `hitEdgeBeans`: moving solids also knock teetering beans and ledge-hangers' fingers.
//  - `dropFromVanishedGround`: the floor falling away (cake tiles, teacup wedges).
//  - `emitWarn` / `emitFire`: the time-derived HAZARD events (both modes, §20.10).
import { MAX_BEANS, NO_SLOT, TUNE } from '../constants';
import { EV, emit } from '../events';
import { BS, EK, type Cause } from '../ids';
import { addDizzy, applyKnockback, canBeHit, dizzyMult } from '../sim/combat';
import { nudgeBodiesInRadius } from '../sim/collision';
import { forceFall, isGrounded } from '../sim/edges';
import { removeEntity } from '../sim/entities';
import { isPredicting, isPuppet } from '../sim/world';
import type { Bean, World } from '../types';
import { solidDistance } from './common';
import { SolidKind, createGroundHit, type MapDef, type Solid } from './mapTypes';
import { dcos, dsin } from '../sim/dmath';

/** Cooldown keys per (bean, hazard instance). Each map uses its own range. */
export const HazardKey = {
  BUMPER: 0, // 0–4
  SLINGSHOT: 5, // 5–6
  FLIPPER: 7, // 7–8
  BALL: 9, // 9–10
  SWEEPER: 11,
  CUP_BOUNCE: 12,
} as const;
const KEY_COUNT = 16;
const NEVER = -1e9;

/**
 * Server-side bookkeeping of the last full hit per (bean, hazard key). It is derived state (only
 * rate-limits Dizzy and HIT events), so it lives beside the world rather than in it: never
 * replicated, hashed or cloned. A world that lost it (a clone) simply allows the next hit.
 */
const lastHits = new WeakMap<World, Float64Array>();

function hitTable(w: World): Float64Array {
  let t = lastHits.get(w);
  if (!t) {
    t = new Float64Array(MAX_BEANS * KEY_COUNT).fill(NEVER);
    lastHits.set(w, t);
  }
  return t;
}

/** True when bean `slot` may take a full hit from hazard `key` (none within `ticks`). */
export function hazardReady(w: World, slot: number, key: number, ticks: number): boolean {
  return w.tick - hitTable(w)[slot * KEY_COUNT + key] >= ticks;
}

function markHit(w: World, slot: number, key: number): void {
  hitTable(w)[slot * KEY_COUNT + key] = w.tick;
}

export interface HazardKickOpts {
  cause: Cause;
  dizzy: number;
  /** Scale the speed by the victim's Dizzy (`dizzyMult`). */
  scaleByDizzy: boolean;
  /** `HazardKey` + index, or −1 for an instantaneous hazard without a repeat limit. */
  key: number;
  /** A full hit (Dizzy, HIT, `onHit`) at most once per this many ticks per bean and key. */
  cooldown: number;
  /** Inside the cooldown, keep setting the velocity (a body riding a moving hazard). */
  carry: boolean;
  vz?: number;
  noTumble?: boolean;
}

/** States whose hazard response is "velocity (+ tumble)", which predict mode reproduces exactly. */
function simpleResponse(state: BS): boolean {
  switch (state) {
    case BS.NORMAL: case BS.LUNGE: case BS.TUMBLE: case BS.GRAB_START: case BS.GRAB_MISS:
    case BS.SLIP: case BS.PUNCH_WINDUP: case BS.PUNCH:
      return true;
    default:
      return false;
  }
}

/**
 * The velocity part of a hazard hit, as `applyKnockback` computes it for a victim at rest
 * (`hazardKick` zeroes the velocity first): speed × Dizzy mult (if asked) × kbTaken × kbMult ×
 * airborne mult, capped at the tunneling bound; tumble at ≥ 6 m/s unless `noTumble`, otherwise a
 * lunge or windup is cancelled. `full` also applies the vertical response (`vz` or the airborne
 * lift); a carry re-application leaves z alone.
 */
function setHazardVelocity(w: World, b: Bean, ux: number, uy: number, speed: number, o: HazardKickOpts, full: boolean): void {
  if (!simpleResponse(b.state)) return;
  const airborne = !isGrounded(b);
  let kb = speed * b.kbTaken * w.rules.kbMult;
  if (o.scaleByDizzy) kb *= dizzyMult(b.dizzy);
  if (airborne) kb *= TUNE.knock.airborneMult;
  kb = Math.min(kb, TUNE.collide.maxSpeed);
  b.vel.x = ux * kb;
  b.vel.y = uy * kb;
  if (full) {
    if (o.vz !== undefined) b.vz = o.vz;
    else if (airborne) b.vz = Math.max(b.vz, TUNE.shove.airborneLift);
  }
  if (!o.noTumble && kb >= TUNE.knock.tumbleThreshold) {
    if (b.state !== BS.TUMBLE) {
      b.state = BS.TUMBLE;
      b.st = 0;
    }
  } else if (b.state === BS.LUNGE || b.state === BS.PUNCH_WINDUP) {
    b.state = BS.NORMAL;
    b.st = 0;
  }
}

/**
 * A hazard impulse on bean `slot` along (dx, dy): its horizontal velocity becomes exactly
 * `dir · speed` × the usual multipliers. Server: a full §2.11 hazard hit via `applyKnockback`
 * (never claims credit, only refreshes it) at most once per `cooldown` per key; inside the
 * cooldown only the velocity is re-applied when `carry`. Predict mode: the local bean gets the
 * same velocity and tumble (no Dizzy, events or credit). Returns true for a full server hit.
 */
export function hazardKick(w: World, slot: number, dx: number, dy: number, speed: number, o: HazardKickOpts): boolean {
  const b = w.beans[slot];
  if (!canBeHit(b)) return false;
  const l = Math.hypot(dx, dy);
  if (l === 0) return false;
  const ux = dx / l;
  const uy = dy / l;
  if (isPredicting(w)) {
    if (isPuppet(w, slot) || !simpleResponse(b.state)) return false;
    setHazardVelocity(w, b, ux, uy, speed, o, true);
    // One-shot hazards also predict their Dizzy, so a chain of bumpers kicks at the right strength.
    if (!o.carry) addDizzy(w, b, o.dizzy);
    return false;
  }
  if (o.key >= 0 && !hazardReady(w, slot, o.key, o.cooldown)) {
    if (o.carry) setHazardVelocity(w, b, ux, uy, speed, o, false);
    return false;
  }
  if (o.key >= 0) markHit(w, slot, o.key);
  b.vel.x = 0;
  b.vel.y = 0;
  applyKnockback(w, slot, ux, uy, speed, { dizzy: o.dizzy, cause: o.cause, by: NO_SLOT, scaleByDizzy: o.scaleByDizzy, hazard: true, vz: o.vz, noTumble: o.noTumble });
  return true;
}

/**
 * A radial pop at (x, y): every hittable bean within `radius` (below `maxZ`) is nudged `speed`
 * away (no tumble, no Dizzy unless given); teetering, hanging and climbing beans fall. Server: the
 * §29.3 `nudgeBodiesInRadius`. Predict mode: the local bean's nudge velocity only.
 */
export function hazardNudge(w: World, x: number, y: number, radius: number, speed: number, dizzy: number, cause: Cause, maxZ: number): void {
  if (!isPredicting(w)) {
    nudgeBodiesInRadius(w, x, y, radius, speed, dizzy, cause, NO_SLOT, maxZ);
    return;
  }
  const slot = w.predictSlot ?? NO_SLOT;
  const b = w.beans[slot];
  if (!b || !canBeHit(b) || !simpleResponse(b.state) || b.z >= maxZ) return;
  const dx = b.pos.x - x;
  const dy = b.pos.y - y;
  const d = Math.hypot(dx, dy);
  if (d > radius) return;
  const ux = d > 0 ? dx / d : -dcos(b.facing);
  const uy = d > 0 ? dy / d : -dsin(b.facing);
  const airborne = !isGrounded(b);
  let kb = speed * b.kbTaken * w.rules.kbMult;
  if (airborne) kb *= TUNE.knock.airborneMult;
  b.vel.x = ux * kb + TUNE.shove.keepVel * b.vel.x;
  b.vel.y = uy * kb + TUNE.shove.keepVel * b.vel.y;
  if (airborne) b.vz = Math.max(b.vz, TUNE.shove.airborneLift);
  if (b.state === BS.LUNGE || b.state === BS.PUNCH_WINDUP) {
    b.state = BS.NORMAL;
    b.st = 0;
  }
}

/** Hand-hold radius used when a moving hazard sweeps over a ledge-hanger's fingers on the rim. */
const FINGER_RADIUS = 0.15;

/**
 * Moving solids (sweeper arms, flippers, pinballs) also hit the beans the collision phase leaves
 * out: teetering beans (anchored, exempt from solids) and ledge-hangers or climbers whose fingers
 * are on the rim under the solid. Any such hit sends them FALLING (§2.9). Server-only.
 */
export function hitEdgeBeans(w: World, s: Solid, cause: Cause): void {
  if (isPredicting(w)) return;
  for (const b of w.beans) {
    if (!b.active || b.out) continue;
    const teeter = b.state === BS.TEETER;
    if (!teeter && b.state !== BS.LEDGE && b.state !== BS.CLIMB) continue;
    const x = teeter ? b.pos.x : b.edgePoint.x;
    const y = teeter ? b.pos.y : b.edgePoint.y;
    const z = teeter ? b.z : b.groundZ;
    if (z >= s.top - TUNE.collide.topSlack) continue;
    if (solidDistance(s, x, y) >= (teeter ? b.radius : FINGER_RADIUS)) continue;
    const sx = x - (s.kind === SolidKind.CIRCLE ? s.ax : (s.ax + s.bx) / 2);
    const sy = y - (s.kind === SolidKind.CIRCLE ? s.ay : (s.ay + s.by) / 2);
    hazardKick(w, b.slot, sx, sy, TUNE.edge.hitFallMinSpeed, { dizzy: 0, cause, scaleByDizzy: false, key: -1, cooldown: 0, carry: false });
  }
}

const edgeHit = createGroundHit();

/**
 * How far a bean on vanishing floor scrambles for the nearest rim (INT-A, cheap-death pass): with
 * remaining ground within this distance the §2.9 edge rules take it in the edge phase of the same
 * tick, as for any bean over VOID (slow: a teeter on that rim, snapped there; mid speed: a ledge
 * hang unless on ledge cooldown; fast: a fall). Two body radii: most of a Crumble Cake hex (inradius
 * 1.1 m) is within reach of a standing neighbour, the middle of a wide hole is not.
 */
export const VANISH_RIM_REACH = 2 * TUNE.bean.radius;

/**
 * The floor under part of the arena just vanished (`gone(x, y)` is true there): beans standing on
 * it drop with their velocity, unless remaining ground is within `VANISH_RIM_REACH`, in which case
 * the §2.9 edge classification takes them (a teeter or a ledge hang at that rim, so a crumbling
 * floor is a scramble, not an instant KO). Teetering beans whose edge point was on it fall
 * outward, and ledge-hangers or climbers whose rim crumbled fall straight down. Runs for every
 * non-puppet bean (the local bean predicts its own drop). Server: crates and peels lying there are
 * lost too.
 */
export function dropFromVanishedGround(w: World, map: MapDef, gone: (x: number, y: number) => boolean): void {
  for (const b of w.beans) {
    if (!b.active || b.out || isPuppet(w, b.slot)) continue;
    switch (b.state) {
      case BS.TEETER:
        if (gone(b.edgePoint.x, b.edgePoint.y)) forceFall(w, b.slot, -b.edgeNormal.x, -b.edgeNormal.y, TUNE.edge.teeterFallSpeed);
        break;
      case BS.LEDGE: case BS.CLIMB:
        if (gone(b.edgePoint.x, b.edgePoint.y)) forceFall(w, b.slot, -b.edgeNormal.x, -b.edgeNormal.y, 0);
        break;
      case BS.FALLING: case BS.GHOST: case BS.RESPAWN: case BS.FROZEN: case BS.GRABBED: case BS.SPUN: case BS.FLOAT: case BS.YANKED:
        break;
      default:
        if (!isGrounded(b) || b.vz > 0 || !gone(b.pos.x, b.pos.y)) break;
        if (map.nearestGround(w, b.pos.x, b.pos.y, edgeHit).dist <= VANISH_RIM_REACH) break;
        forceFall(w, b.slot, b.vel.x, b.vel.y, Math.hypot(b.vel.x, b.vel.y));
        break;
    }
  }
  if (isPredicting(w)) return;
  for (let i = w.ents.length - 1; i >= 0; i--) {
    const e = w.ents[i];
    if ((e.kind === EK.CRATE || e.kind === EK.PEEL) && e.z <= TUNE.bean.airborneZ && gone(e.pos.x, e.pos.y)) removeEntity(w, e.id);
  }
}

/** HAZARD_WARN (b = kind, c = index, d = lead ticks). Time-derived: emitted in server and predict mode alike (§20.10). */
export function emitWarn(w: World, kind: number, index: number, leadTicks: number, x: number, y: number): void {
  emit(w, EV.HAZARD_WARN, NO_SLOT, kind, index, Math.round(leadTicks), x, y);
}

/** HAZARD_FIRE (b = kind, c = index). Time-derived like HAZARD_WARN. */
export function emitFire(w: World, kind: number, index: number, x: number, y: number): void {
  emit(w, EV.HAZARD_FIRE, NO_SLOT, kind, index, 0, x, y);
}
